import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Loader2, ArrowLeft, Sparkles } from "lucide-react";

export default function PublicLookbook() {
  const { token } = useParams();
  const [lb, setLb] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/lookbooks/${token}`)
      .then(({ data }) => setLb(data))
      .catch(() => setError(true));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
      <div className="text-center">
        <div className="overline text-zinc-500">§ 404</div>
        <div className="font-serif text-6xl mt-2">Lookbook not found.</div>
        <Link to="/" className="mt-8 inline-flex overline items-center gap-2 hover:text-lime-400" data-testid="pub-lookbook-home">
          <ArrowLeft className="w-3.5 h-3.5" /> InstaLux
        </Link>
      </div>
    </div>
  );
  if (!lb) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-lime-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-lookbook-view">
      <header className="px-6 md:px-12 pt-10 pb-6 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="overline flex items-center gap-2 hover:text-lime-400 transition-colors" data-testid="pub-lookbook-brand">
          <span className="w-1.5 h-1.5 bg-lime-400 rounded-full" />
          Insta<span className="italic font-serif">Lux</span>
        </Link>
        <div className="overline text-zinc-500">A lookbook by {lb.creator_name || "an atelier"}</div>
      </header>

      <section className="px-6 md:px-12 pt-24 pb-24">
        <div className="max-w-[1600px] mx-auto">
          <div className="overline text-lime-400 mb-4">§ Collection</div>
          <h1 className="font-serif text-6xl md:text-9xl leading-[0.85] mb-6" data-testid="pub-lookbook-title">{lb.title}</h1>
          {lb.description && (
            <p className="max-w-3xl text-xl font-serif italic text-zinc-300 leading-relaxed">{lb.description}</p>
          )}
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-[1600px] mx-auto">
          {lb.designs.length === 0 ? (
            <div className="border border-dashed border-zinc-800 p-16 text-center">
              <div className="font-serif italic text-3xl text-zinc-500">Empty collection.</div>
            </div>
          ) : (
            <div className="space-y-32" data-testid="pub-lookbook-designs">
              {lb.designs.map((d, i) => {
                const layout = i % 3;
                return (
                  <div key={d.id} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center reveal" style={{ animationDelay: `${i * 80}ms` }} data-testid={`pub-lookbook-look-${d.id}`}>
                    <div className={`md:col-span-8 ${layout === 1 ? "md:col-start-5" : layout === 2 ? "md:col-start-3" : ""} relative aspect-[3/4] overflow-hidden`}>
                      <img src={fileUrl(d.image_path)} alt={d.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className={`md:col-span-3 ${layout === 1 ? "md:col-start-2 md:row-start-1" : ""}`}>
                      <div className="overline text-zinc-500 mb-2">Look N°{String(i+1).padStart(2, "0")}</div>
                      <div className="font-serif text-3xl italic leading-tight">{d.title}</div>
                      {(d.fabric || d.silhouette || d.length) && (
                        <div className="mt-3 text-sm text-zinc-400 space-y-1">
                          {d.fabric && <div><span className="overline text-zinc-600 mr-2">Fabric</span> {d.fabric}</div>}
                          {d.silhouette && <div><span className="overline text-zinc-600 mr-2">Silhouette</span> {d.silhouette}</div>}
                          {d.length && <div><span className="overline text-zinc-600 mr-2">Length</span> {d.length}</div>}
                          {d.color && <div><span className="overline text-zinc-600 mr-2">Palette</span> {d.color}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <footer className="px-6 md:px-12 py-16 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="overline text-zinc-500">Rendered in InstaLux — the AI fashion studio</div>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lime-400 text-zinc-950 overline hover:bg-zinc-50 transition-colors" data-testid="pub-lookbook-cta">
          <Sparkles className="w-3.5 h-3.5" /> Design your own
        </Link>
      </footer>
    </div>
  );
}
